import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import TextField from "@mui/material/TextField";
import { SearchSelectionBox } from "app/components/reports/SearchSelectionBox";
import { MonthOption } from "app/components/reports/MonthOption";
import { FiscalWeekOption } from "app/components/reports/FiscalWeekOption";
import { CustomDateOption } from "app/components/reports/CustomDateOption";
import {
  getStartDateFromAnyMonth,
  getEndDateFromAnyMonth,
  getStartDateFromWeekNumber,
  getEndDateFromWeekNumber,
  getCurrentYearStartDate,
} from "utils/dateUtil";
import {
  PERIOD_OPTION_CUSTOM_DATES,
  PERIOD_OPTION_FISCAL_WEEK,
  PERIOD_OPTION_MONTH,
} from "utils/periodOptions";

// How much of the period picker a screen offers. FULL lets the user choose between a
// month, a fiscal week and a custom date range; MONTH_YEAR is the reduced form for the
// reports that are defined per calendar month and have no other period to offer.
// @since NEXT_VERSION
export const PERIOD_MODE_FULL = "FULL";
// @since NEXT_VERSION
export const PERIOD_MODE_MONTH_YEAR = "MONTH_YEAR";

// Oldest year offered in the Year dropdown; every screen listed the same range.
// @since NEXT_VERSION
export const FIRST_SELECTABLE_YEAR = 2022;


// Resolve a { from, to } range from the selected period type + inputs. Exported so a
// parent can seed its initial (current-month) range without duplicating the switch.
export const computeRange = (searchOption, { month, year, fiscalWeek, customStart, customEnd }) => {
  switch (searchOption) {
    case PERIOD_OPTION_FISCAL_WEEK:
      return { from: getStartDateFromWeekNumber(fiscalWeek, year), to: getEndDateFromWeekNumber(fiscalWeek, year) };
    case PERIOD_OPTION_CUSTOM_DATES:
      return { from: customStart, to: customEnd };
    case PERIOD_OPTION_MONTH:
    default:
      return { from: getStartDateFromAnyMonth(month, year), to: getEndDateFromAnyMonth(month, year) };
  }
};

/**
 * The period a screen starts on: the current month. `overrides` lets a screen seed from
 * whatever it persists (Redux, session) without restating the other fields.
 * @since NEXT_VERSION
 */
export const defaultPeriod = (overrides = {}) => ({
  searchOption: PERIOD_OPTION_MONTH,
  year: new Date().getFullYear(),
  month: new Date().getMonth(),
  fiscalWeek: 1,
  customStart: dayjs(getCurrentYearStartDate()),
  customEnd: dayjs(new Date()),
  ...overrides,
});

/**
 * The { from, to } a period value resolves to. Every screen turns its period into dates
 * this way, so the mapping lives next to the picker that produced it.
 * @since NEXT_VERSION
 */
export const periodRange = (period) => computeRange(period?.searchOption, period ?? {});

/**
 * Controlled period picker. Renders Year first, then either the period-type selector plus
 * its matching sub-control (FULL) or just the month (MONTH_YEAR) — the same order on every
 * screen. The parent owns the value so it can persist and apply it on its own terms.
 *
 * Every field is controlled, so a period the parent changes on its own — restoring a saved
 * filter, resetting after Apply — is reflected in the inputs. `value` may be partial (a
 * month-only screen supplies just the month and year); the rest falls back to the defaults.
 * @since NEXT_VERSION
 */
export const PeriodFilter = ({ value, onChange, mode = PERIOD_MODE_FULL, idPrefix = "effort" }) => {
  // Undefined entries are dropped rather than spread over the defaults: letting one through
  // would hand a controlled input an undefined value and flip it to uncontrolled.
  const supplied = Object.fromEntries(Object.entries(value ?? {}).filter(([, v]) => v !== undefined));
  const period = { ...defaultPeriod(), ...supplied };
  const update = (patch) => onChange({ ...period, ...patch });

  const years = [];
  for (let i = new Date().getFullYear(); i >= FIRST_SELECTABLE_YEAR; i--) years.push(i);

  const monthControl = (
    <MonthOption
      selectedMonth={period.month}
      monthChangeHandler={(e) => update({ month: parseInt(e.target.value, 10) })}
      id={`${idPrefix}-month`}
    />
  );

  const renderCriteria = () => {
    switch (period.searchOption) {
      case PERIOD_OPTION_FISCAL_WEEK:
        // Left as the raw string, unlike month and year: parsing an emptied field would
        // yield NaN and render as "NaN" in the box the user just cleared.
        return (
          <FiscalWeekOption
            fiscalWeek={period.fiscalWeek}
            weekChangeHandler={(e) => update({ fiscalWeek: e.target.value })}
            id={`${idPrefix}-fiscal-week`}
          />
        );
      case PERIOD_OPTION_CUSTOM_DATES:
        return (
          <CustomDateOption
            activityStartDate={period.customStart}
            activityEndDate={period.customEnd}
            updateStartDateHandler={(customStart) => update({ customStart })}
            updateEndDateHandler={(customEnd) => update({ customEnd })}
          />
        );
      case PERIOD_OPTION_MONTH:
      default:
        return monthControl;
    }
  };

  const yearControl = (
    <TextField
      select
      label="Year"
      id={`${idPrefix}-year`}
      helperText="*Required"
      value={period.year}
      onChange={(e) => update({ year: parseInt(e.target.value, 10) })}
      SelectProps={{ native: true }}
      sx={{ flex: { xs: "1 1 120px", md: "0 0 120px" }, minWidth: 0 }}
    >
      {years.map((i) => (
        <option key={i} value={i}>
          {i}
        </option>
      ))}
    </TextField>
  );

  // A custom range states its own start and end dates, so a Year beside it decides nothing and can
  // read as though it contradicts the range the user just picked. Month and Fiscal Week do need it:
  // both are a position *within* a year, and computeRange resolves them against period.year.
  //
  // Hiding it is safe because `update` patches the whole period rather than replacing it, so a
  // switch through Custom Dates reports the year back unchanged and Month returns to the year the
  // user had. @since NEXT_VERSION
  const showYear = period.searchOption !== PERIOD_OPTION_CUSTOM_DATES;

  // Period leads, because it decides which controls follow it (HRMS-275). Reading Year first asked
  // the user to answer a question whose relevance the next field had not established yet.
  return (
    <>
      {mode === PERIOD_MODE_MONTH_YEAR ? (
        <>
          {yearControl}
          {monthControl}
        </>
      ) : (
        <>
          <SearchSelectionBox
            searchOption={period.searchOption}
            searchOptionHandler={(e) => update({ searchOption: e.target.value })}
            id={`${idPrefix}-period-type`}
          />
          {showYear && yearControl}
          {renderCriteria()}
        </>
      )}
    </>
  );
};

PeriodFilter.propTypes = {
  value: PropTypes.shape({
    searchOption: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    month: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fiscalWeek: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customStart: PropTypes.any,
    customEnd: PropTypes.any,
  }),
  onChange: PropTypes.func.isRequired,
  mode: PropTypes.oneOf([PERIOD_MODE_FULL, PERIOD_MODE_MONTH_YEAR]),
  idPrefix: PropTypes.string,
};

export default PeriodFilter;
